import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableCurrencyPolicy, UpdatableCurrencyPolicy } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { invalidatePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useAppSelector } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"
import { mutate } from "swr"

const baseKey = "currency-policy"

export const invalidateCurrencyPolicies = () =>
    Promise.all([
        mutate((key) => Array.isArray(key) && key[0] === baseKey),
        invalidatePilotTrackingQuery("currency_policies"),
        invalidatePilotTrackingQuery("currency_policy_users"),
        invalidatePilotTrackingQuery("currency_policy_recipients"),
    ])

const createCurrencyPolicy = ({
    body,
    orgId,
}: {
    body: CreatableCurrencyPolicy
    orgId: string
}) => {
    return fetchApiGateway("post /org/:orgId/fleet/currency-policy", {
        body,
        params: { orgId },
    })
}

export const useCreateCurrencyPolicy = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "create-currency-policy",
        (key, { arg }: { arg: CreatableCurrencyPolicy }) =>
            orgId ? createCurrencyPolicy({ body: arg, orgId }) : undefined,
        { onSuccess: invalidateCurrencyPolicies }
    )

    return { createCurrencyPolicy: trigger, isMutating }
}

const getCurrencyPolicy = ({ policyId, orgId }: { orgId: string; policyId: string }) => {
    return fetchApiGateway("get /org/:orgId/fleet/currency-policy/:policyId", {
        params: { orgId, policyId },
    })
}

export const useCurrencyPolicy = (policyId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate([baseKey, orgId, policyId], () =>
        orgId ? getCurrencyPolicy({ policyId, orgId }) : undefined
    )

    return { data, isLoading }
}

const updateCurrencyPolicy = ({
    policyId,
    body,
    orgId,
}: {
    policyId: string
    body: UpdatableCurrencyPolicy
    orgId: string
}) => {
    return fetchApiGateway("patch /org/:orgId/fleet/currency-policy/:policyId", {
        body,
        params: { orgId, policyId },
    })
}

export const useUpdateCurrencyPolicy = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "update-currency-policy",
        (key, { arg }: { arg: { policyId: string; body: UpdatableCurrencyPolicy } }) =>
            orgId
                ? updateCurrencyPolicy({ policyId: arg.policyId, body: arg.body, orgId })
                : undefined,
        { onSuccess: invalidateCurrencyPolicies }
    )

    return { updateCurrencyPolicy: trigger, isMutating }
}

const deleteCurrencyPolicy = ({ policyId, orgId }: { policyId: string; orgId: string }) => {
    return fetchApiGateway("delete /org/:orgId/fleet/currency-policy/:policyId", {
        params: { orgId, policyId },
    })
}

export const useDeleteCurrencyPolicy = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "delete-currency-policy",
        (key, { arg: policyId }: { arg: string }) =>
            orgId ? deleteCurrencyPolicy({ policyId, orgId }) : undefined,
        { onSuccess: invalidateCurrencyPolicies }
    )

    return { deleteCurrencyPolicy: trigger, isMutating }
}
